// Chamadas da página "Onde a Hydrodata ganha" (ações `og_*` em /comercial/propostas-geradas).
// Contrato: docs/onde-a-hydrodata-ganha.md (se divergir, vale o doc). Uma função por ação;
// o erro vem com a frase do servidor (`erroDe`), que é o que a tela mostra.
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fraseDeFalha } from './ondeGanhaRegras';

async function post<T = any>(body: Record<string, any>, timeout = 30000): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, body, { headers: { Authorization: token }, timeout });
  return (r.data?.body ? JSON.parse(r.data.body) : r.data) as T;
}

export const erroDe = (e: any): string => {
  const d = e?.response?.data;
  const doServidor = d?.error || d?.erro;
  if (typeof doServidor === 'string' && doServidor.trim()) return doServidor;
  return fraseDeFalha(e?.response?.status ?? null, !!e?.response, e?.code, e?.message);
};

/** 409 = já tem uma atualização em andamento, ou outra pessoa mudou o caso antes. */
export const ehConflito = (e: any): boolean => e?.response?.status === 409;
/** 429 = pediu de novo antes dos 5 minutos. */
export const ehEspera = (e: any): boolean => e?.response?.status === 429;
/** 403 = sem a permissão da ação. */
export const ehSemAcesso = (e: any): boolean => e?.response?.status === 403;

// ── Tipos do contrato ────────────────────────────────────────────────────────
export interface Grupo { codigo: string; nome: string; curto: string }

export type StatusCaso = 'ganho' | 'perdido';
export type OrigemSituacao = 'sugestao' | 'equipe' | 'revisada';
export type ReuniaoCaso = 'com' | 'sem' | null;
export type FonteReuniao = 'anotacao' | 'registro' | 'agenda' | null;

export interface Caso {
  id: number;
  titulo: string;
  cliente: string | null;
  segmento: string;
  area: string | null;
  valor: number | null;
  status: StatusCaso;
  /** Data do envio da proposta; sem ela, a data do fechamento. */
  data_ref: string | null;
  grupo: string | null;
  linha: string | null;
  origem_situacao: OrigemSituacao | null;
  /** Momento em que a situação foi gravada. Volta junto no salvar, para detectar edição de outra pessoa. */
  situacao_em: string | null;
  reuniao: ReuniaoCaso;
  reuniao_fonte: FonteReuniao;
  cliente_novo: boolean;
  motivo: string | null;
}

export interface NumReuniaoSegmento {
  segmento: string; casos: number; com: number; com_ganhos: number; sem: number; sem_ganhos: number; sem_info: number;
}
export interface NumReuniaoRelacao {
  relacao: 'novo' | 'casa'; casos: number; com: number; com_ganhos: number; sem: number; sem_ganhos: number;
}
export interface NumReuniaoTotal {
  casos?: number; com?: number; com_ganhos?: number; sem?: number; sem_ganhos?: number; sem_info?: number;
}
export interface NumReuniao {
  por_segmento: NumReuniaoSegmento[];
  por_relacao: NumReuniaoRelacao[];
  total: NumReuniaoTotal;
  medidos: number;
}
export interface PerdasPorMotivo {
  concorrente: number; preco: number; adiou: number; sem_retorno: number; sem_motivo: number; outro: number;
}
export interface NumSituacao {
  grupo: string; casos: number; ganhos: number; perdidos: number;
  /** Inteiro, em %. */
  taxa: number;
  receita: number; mediana: number; clientes: number; novos_ganhos: number;
  /** Inteiro, em %: os 3 maiores clientes sobre a receita do grupo. */
  top3_pct: number;
  perdas: PerdasPorMotivo;
}
export interface Numeros {
  total: number; ganhos: number; perdidos: number; receita: number;
  reuniao: NumReuniao;
  situacoes: NumSituacao[];
  sem_situacao: number;
}

export interface Nota { nota: number; motivo: string }
export interface CandidatoSemana3 {
  segmento: string;
  aceite: Nota;
  capacidade: { nota: number | null; motivo: string };
  posicionamento: Nota;
  timing: Nota;
}
export interface TextoIa {
  resumo: string[];
  reuniao: string;
  situacoes: { grupo: string; leitura: string }[];
  teste: {
    negocio_id: number; descricao: string; veredito: string;
    qualquer_um: string[]; so_nos: string[]; contradicoes: string[];
    abertura: { para_quem: string; risco: string; prova: string; opcoes: string };
  };
  semana3: { candidatos: CandidatoSemana3[]; recomendacao: string; segundo_plano: string; frases: string[] };
  proximos_passos: string[];
  /** Avisos do servidor (um lote que não classificou, por exemplo). */
  avisos?: string[];
}

export interface Retrato {
  id: number;
  origem: 'ia' | 'importado';
  pedido_por: string;
  pedido_em: string;
  concluido_em: string | null;
  modelo: string | null;
  texto: TextoIa;
  assinatura: string | null;
}
export interface EmAndamento { id: number; pedido_por: string; pedido_em: string }
export interface UltimoErro { id: number; concluido_em: string | null; erro: string }

export interface Obter {
  pode: { atualizar: boolean; revisar: boolean };
  grupos: Grupo[];
  numeros: Numeros;
  assinatura_atual: string | null;
  casos: Caso[];
  retrato: Retrato | null;
  em_andamento: EmAndamento | null;
  ultimo_erro: UltimoErro | null;
  janela: { desde: string };
}

export type StatusDoPedido = 'processando' | 'pronto' | 'erro';
export interface StatusAtualizacao {
  id: number; status: StatusDoPedido; erro: string | null; concluido_em: string | null;
}

// ── Ações ────────────────────────────────────────────────────────────────────
export const obter = (): Promise<Obter> => post<Obter>({ action: 'og_obter' }, 60000);

export const atualizar = (): Promise<{ id: number; status: StatusDoPedido }> => post({ action: 'og_atualizar' });

export const status = (id: number): Promise<StatusAtualizacao> => post<StatusAtualizacao>({ action: 'og_status', id });

export const salvarSituacao = (p: {
  proposta_id: number; grupo: string; linha: string; situacao_em: string | null;
}): Promise<Caso> => post<Caso>({ action: 'og_situacao_salvar', ...p });
